<template>
  <div class="signup-wrapper">
    <div class="signup-content">
      <div class="signup-step-back">
        <v-icon color="#333333" size="22" @click="$router.go(-1)">
          mdi-arrow-left
        </v-icon>
      </div>
      <h2 class="signup-title">
        {{ $t("signup.identification.title") }}
      </h2>

      <v-form
        ref="form"
        v-model="valid"
        @submit.prevent="saveCustomerIdentity()"
        class="signup-form file-inputs"
      >
        <TextField
          id="rut"
          :label="$t('signup.identification.fields.rut.label')"
          :value="signupStatus.completedValidation ? signupIdentificationData.rut : rut"
          :placeholder="$t('signup.identification.fields.rut.placeholder')"
          :rules="rutRules"
          :disabled="signupStatus.completedValidation"
          @updateValue="rut = $event"
        />

        <div v-if="!signupStatus.completedValidation">
          <div class="label-row">
            <div class="signup-form-label">
              {{ $t("signup.identification.fields.serialNumber.label") }}
            </div>
            <div
              class="signup-form-label where-is-label"
              @click="displayHintDialog = true"
            >
              {{ $t("signup.identification.fields.serialNumber.hint") }}
            </div>
          </div>

          <TextField
            id="serial-number"
            :value="serialNumber"
            :placeholder="$t('signup.identification.fields.serialNumber.placeholder')"
            :rules="serialNumberRules"
            @updateValue="serialNumber = $event"
          />
        </div>

        <div class="signup-form-label">
          {{ $t("signup.identification.images.label") }}
        </div>

        <FileInput
          id="front-identification-card"
          v-model="frontUpload"
          :rules="imageRules"
          :placeholder="$t('signup.identification.images.front.placeholder')"
          :disabled="signupStatus.completedValidation"
          ref="filef"
          @updateValue="frontUpload = $event"
          @delete="frontUpload = null"
          @on-change="handleImageSize(frontUpload, 'frontUpload')"
          class="file-input"
        />

        <FileInput
          id="back-identification-card"
          v-model="backUpload"
          :rules="imageRules"
          :placeholder="$t('signup.identification.images.back.placeholder')"
          :disabled="signupStatus.completedValidation"
          ref="fileb"
          @updateValue="backUpload = $event"
          @delete="backUpload = null"
          @on-change="handleImageSize(backUpload, 'backUpload')"
          class="file-input"
        />

        <p class="signup-hint">
          {{ $t("signup.identification.cmfExplanation") }}
        </p>

        <NewButton
          id="next-step"
          class="signup-step-button"
          :text="$t('signup.shared.buttons.next')"
          :type="'submit'"
          :loading="loading"
          :disabled="!valid"
        />

        <div
          class="signup-cancel-container"
          @click="$router.push({ name: 'signupDashboard' })"
        >
          <p class="signup-cancel">
            {{ $t("base.cancel") }}
          </p>
        </div>
      </v-form>
    </div>

    <ReusableDialog
      v-model="errorDialog"
      :title="errorMessages.title"
      :text="errorMessages.text"
      @display="errorDialog = $event"
    >
      <template v-slot:buttonRow>
        <div class="signup-button-row">
          <div class="signup-chat-btn">
            <NewButton :text="$t('base.goToChat')" @on-click="openChat()" />
          </div>

          <div class="keep-trying-btn">
            <NewButton :text="$t('base.close')" @on-click="errorDialog = false" />
          </div>
        </div>
      </template>
    </ReusableDialog>

    <SerialNumberHintDialog
      :dialog="displayHintDialog"
      @displayDialog="displayHintDialog = $event"
    />
  </div>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import { signupFunctions } from "@/mixins/signup.js";
import { imagesFunctions } from "@/mixins/images.js";
import NewButton from "@/components/shared/NewButton.vue";
import SerialNumberHintDialog from "@/views/signup/identification/SerialNumberHintDialog.vue";
import ReusableDialog from "@/components/shared/ReusableDialog.vue";
import TextField from "@/components/shared/TextField.vue";
import FileInput from "@/components/shared/FileInput.vue";

const { validate, format } = require("rut.js");

export default {
  name: "IdentificationIntro",

  components: {
    SerialNumberHintDialog,
    NewButton,
    ReusableDialog,
    TextField,
    FileInput,
  },

  mixins: [signupFunctions, imagesFunctions],

  data() {
    return {
      serialNumber: null,
      rut: null,
      loading: false,
      errorDialog: false,
      displayHintDialog: false,
      valid: false,
      errorMessages: {
        title: "",
        text: "",
      },
      frontUpload: null,
      backUpload: null,
      rutRules: [
        v => !!v || this.$t("signup.shared.errors.mustComplete"),
        v => validate(v) || this.$t("signup.identification.fields.errors.rut"),
      ],
      serialNumberRules: [
        v => !!v ||
          this.signupStatus.completedValidation ||
          this.$t("signup.shared.errors.mustComplete"),
        v => /(^A{0,1}[0-9]{9})|(^A{0,1}[0-9]{3}\.[0-9]{3}\.[0-9]{3})/.test(v) ||
          this.$t("signup.identification.fields.errors.documentNumber"),
      ],
      imageRules: [
        v => !!v ||
          this.signupStatus.completedValidation ||
          this.$t("signup.shared.errors.mustComplete"),
      ],
    };
  },

  computed: {
    ...mapGetters(["signupIdentificationData", "signupStatus"]),
  },

  methods: {
    ...mapActions(["setIdDocumentImages", "setIdentifier"]),

    handleErrorResponse(response) {
      this.setIdentifier({
        rut: null,
        serialNumber: null,
      });
      this.setIdCardImages({
        frontImage: null,
        backImage: null,
      });
      this.errorDialog = true;
      this.loading = false;
      this.errorMessages = {
        title: this.$t(
          `signup.shared.dialogs.${response?.errorType || "defaultError"}.title`,
        ),
        text: this.$t(
          `signup.shared.dialogs.${response?.errorType || "defaultError"}.text`,
        ),
      };
    },

    async saveCustomerIdentity() {
      this.setIdDocumentImages({
        frontImage: this.frontUpload,
        backImage: this.backUpload,
      });

      if (this.signupStatus.completedValidation) {
        this.$router.push({ name: "signupIdentificationPersonal" });
      } else {
        this.loading = true;
        this.setIdentifier({
          rut: format(this.rut || this.signupIdentificationData.rut),
          serialNumber: this.serialNumber,
        });
        const validateCustomerResponse = await this.sendValidationData();
        this.loading = false;
        if (validateCustomerResponse.error) {
          this.handleErrorResponse(validateCustomerResponse);
        } else {
          this.$router.push({ name: "signupIdentificationPersonal" });
        }
      }
    },

    openChat() {
      this.$intercom.showNewMessage(
        this.$t("signup.identification.errors.intercom.existingIdentifier"),
      );
    },

    reloadData() {
      if (this.signupIdentificationData.rut) this.rut = this.signupIdentificationData.rut;
      if (this.signupIdentificationData.serialNumber) {
        this.serialNumber = this.signupIdentificationData.serialNumber;
      }
      if (this.signupIdentificationData.frontImage instanceof File) {
        this.frontUpload = this.signupIdentificationData.frontImage;
      }
      if (this.signupIdentificationData.backImage instanceof File) {
        this.backUpload = this.signupIdentificationData.backImage;
      }
    },
  },

  mounted() {
    this.reloadData();
  },
};
</script>

<style lang="scss" scoped>
.prepend-container {
  height: 26px !important;
  display: flex;
  align-items: center;
  gap: 5px;
  white-space: nowrap;
}

.upload-button-wrapper {
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
}
.signup-chat-btn::v-deep .new-btn {
  background-color: #ffffff !important;
  border: 1px solid #000000 !important;
  span {
    color: #000000 !important;
  }
}

.file-input + .file-input {
  margin-top: 10px;
}
</style>
